// THE SETUP BREADCRUMBS (owner vrequest 2026-09-15, second request of the day).
//
// A collapsed Setup section, and a folded Calls / Puts side, shows a CRADLE under its header: a
// strip of chips summarising what the section currently holds, so a collapsed section never
// makes the trader guess what is inside. Owner rulings, 2026-09-15:
//   1. Chips, without the ORIONs' "Click to edit" hint.
//   2. A section with nothing chosen shows ONE chip, "Not Set".
//   3. Wording as close to the ORION (Hedging Grid) input summary as Maven's questions allow —
//      `Label · value`, the family's date string "Wed 16-Sep-26", money as "$10.00".
//   4. The zone rides the Portfolio cradle as plain text — no stale tint here.
//   5. The two sides get a cradle of their own when folded.
//
// PURE FORMATTERS, no Angular: one function per section, each a projection of the LIVE Setup into
// chips. Nothing here owns state and nothing here decides — the chips say what is set, the
// validator says what is missing. A field not yet set is simply ABSENT from its section's chips;
// only a section with nothing set at all reads "Not Set".
//
// EACH CHIP CARRIES A KEY (owner vrequest 2026-09-15, the change-highlight request — which
// REPLACES the earlier "no changed-vs-run tint on the chips" exclusion): the prefix, in the diff's
// key grammar (maven-setup-param-diff.ts), of the Setup fields the chip summarises. The component
// tints a chip yellow when any changed key starts with it. The formatters know the grammar, not
// the diff: they never decide whether anything changed. The zone chip carries NO key — ruling 4
// stands, the zone has its own stale border.
//
// Maven ORDERS from the ORIONs and bakes its own product (memory rule): the date/money formatting
// below is a hand-copy of hg-copilot-input-summary.component.ts's helpers, per the family's
// isolated-sibling convention, and the chip texts are Maven's own — Maven's Range has an END
// strike only, its offset has no ORION counterpart, and its coverage has three kinds, not four.
//
// ES5 note: arrays + indexOf only (project rule).

import {RollPositionsInputs} from '../../adjustment-pricing-grid/copilot/apg-copilot-domain';
import {parseDayKey} from '../../adjustment-pricing-grid/copilot/apg-copilot-questionnaire';
import {
    isUniformAcrossDates,
    MarketConditionOverride,
    MarketConditionOverrides
} from '../../copilot-shared/opg-copilot-domain';
import {MavenExistingHedges, MavenSideInputs} from '../maven-domain';

export const MAVEN_NOT_SET_CHIP = 'Not Set';

// One cradle chip: its text, and the diff-key prefix of what it summarises (null = never tinted).
export interface MavenChip {
    key: string | null;
    text: string;
}

function chip(key: string | null, text: string): MavenChip {
    return {key: key, text: text};
}

// The chips' texts alone, for the one place that composes them into a bigger chip.
export function chipTexts(chips: MavenChip[]): string[] {
    return chips.map(c => c.text);
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DOW3 = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function isValidDate(d: Date | null | undefined): boolean {
    return d instanceof Date && !isNaN(d.getTime());
}

function isFiniteNumber(n: number | null | undefined): boolean {
    return typeof n === 'number' && isFinite(n);
}

// The family's date string: "Wed 16-Sep-26" (weekday + dd-MMM-yy).
export function fmtMavenDate(d: Date): string {
    if (!isValidDate(d)) {
        return '—';
    }
    const dd = d.getDate() < 10 ? '0' + d.getDate() : '' + d.getDate();
    const yy = d.getFullYear() % 100;
    const yyStr = yy < 10 ? '0' + yy : '' + yy;
    return DOW3[d.getDay()] + ' ' + dd + '-' + MONTHS[d.getMonth()] + '-' + yyStr;
}

function fmtMoney(n: number): string {
    if (!isFiniteNumber(n)) {
        return '—';
    }
    return '$' + n.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2});
}

// "Not Set" when nothing else was said; otherwise the chips as given. The "Not Set" chip carries
// the section's own key: a section emptied AFTER a run is a change, and its one chip must say so.
function orNotSet(chips: MavenChip[], notSetKey: string | null): MavenChip[] {
    return chips.length > 0 ? chips : [chip(notSetKey, MAVEN_NOT_SET_CHIP)];
}

// ---- Choose The Portfolio ----------------------------------------------------------------------

export interface MavenPortfolioCrumbs {
    // The CLOSED select box's own reading, "Alan K.  |  Default Portfolio", or null while none.
    portfolioLabel: string | null;
    templateName: string | null;
    zone: number | null;
}

export function portfolioChips(c: MavenPortfolioCrumbs): MavenChip[] {
    const chips: MavenChip[] = [];

    if (c.portfolioLabel) {
        chips.push(chip('portfolio', 'Portfolio · ' + c.portfolioLabel));
    }
    if (c.templateName) {
        chips.push(chip('template', 'Template · ' + c.templateName));
    }
    // The zone is a fact about the portfolio, so it rides only when one is chosen: a zone with no
    // portfolio would be a leftover from the previous one. No key: never tinted (ruling 4).
    if (c.portfolioLabel && isFiniteNumber(c.zone)) {
        chips.push(chip(null, 'Zone · ' + c.zone));
    }
    return orNotSet(chips, 'portfolio');
}

// ---- Date Range --------------------------------------------------------------------------------

export function datesChips(inputs: RollPositionsInputs | null | undefined): MavenChip[] {
    if (!inputs || !inputs.date) {
        return [chip('date.', MAVEN_NOT_SET_CHIP)];
    }
    const chips: MavenChip[] = [];
    const dt = inputs.date;
    if (dt.kind === 'single') {
        if (isValidDate(dt.single)) {
            chips.push(chip('date.', 'Date · ' + fmtMavenDate(dt.single as Date)));
        }
        // A single date has no exclusions to speak of (the questionnaire's own rule).
        return orNotSet(chips, 'date.');
    }
    if (isValidDate(dt.start) && isValidDate(dt.end)) {
        chips.push(chip('date.', 'Dates · ' + fmtMavenDate(dt.start as Date) + ' → ' + fmtMavenDate(dt.end as Date)));
    }
    const excluded: string[] = [];
    (inputs.weekdayExclusions || []).forEach(w => excluded.push(w));
    (inputs.specificDateExclusions || []).forEach(d => {
        if (isValidDate(d)) {
            excluded.push(fmtMavenDate(d));
        }
    });
    if (excluded.length > 0) {
        // A day the trader brought back must not be summarised as a plain "Excluded · Mon" —
        // the APG ORION's own rule (BR-W4), carried here so the two cradles never disagree.
        const lifted = liftedDays(inputs);
        const except = lifted.length > 0 ? ' (except ' + lifted.join(', ') + ')' : '';
        chips.push(chip('exclusions.', 'Excluded · ' + excluded.join(', ') + except));
    }
    return orNotSet(chips, 'date.');
}

// The brought-back days whose weekday is actually excluded (CR-20260915-002: any excluded weekday,
// not Mondays only) - with that weekday's rule off there is nothing to make an exception to.
// DOW3 is this file's existing day-name table.
function liftedDays(inputs: RollPositionsInputs): string[] {
    const excludedWeekdays: string[] = inputs.weekdayExclusions || [];
    return (inputs.weekdayOverrides || [])
        .filter(d => isValidDate(d) && excludedWeekdays.indexOf(DOW3[d.getDay()]) >= 0)
        .sort((a, b) => a.getTime() - b.getTime())
        .map(d => fmtMavenDate(d));
}

// ---- Existing Hedges ---------------------------------------------------------------------------

export function existingHedgesChips(choice: MavenExistingHedges | null | undefined): MavenChip[] {
    if (choice === 'close') {
        return [chip('existingHedges', 'Existing Hedges · Close')];
    }
    if (choice === 'ignore') {
        return [chip('existingHedges', 'Existing Hedges · Ignore')];
    }
    return [chip('existingHedges', MAVEN_NOT_SET_CHIP)];
}

// CR-20260916-003, the Close selection's chips (owner ruling 8, 2026-09-16): ONE CHIP PER SIDE in
// the side's shade, reading `Calls · 16-Sep-26 (Call Hedge 1 · Call Hedge 2) · 19-Sep-26 (ALL)`
// — each toggled expiration followed by ITS selected hedges of that side in brackets, "ALL" in
// place of the names when every hedge of that side in that expiration is selected. A side with
// nothing selected has no chip. The chip carries the BARE hedge name (no "(cal)" marker).
// Both chips answer to the two selection keys: a toggle in either list is a change to what the
// chip says.
export interface MavenCloseBoxSummary {
    date: string;          // 'yyyy-MM-dd'
    label: string;         // 'dd-MMM-yy', as the toggle button shows it
    callNames: string[];   // the SELECTED call hedges' names, in list order
    callTotal: number;     // how many call hedges the box holds
    putNames: string[];
    putTotal: number;
}

export function closeSelectionChips(boxes: MavenCloseBoxSummary[]): MavenSideCrumb[] {
    const keys = ['existingHedges.expirations', 'existingHedges.hedges'];
    const out: MavenSideCrumb[] = [];
    const calls = closeSideText(boxes, 'calls');
    if (calls) {
        out.push({side: 'calls', key: null, keys: keys, text: 'Calls · ' + calls});
    }
    const puts = closeSideText(boxes, 'puts');
    if (puts) {
        out.push({side: 'puts', key: null, keys: keys, text: 'Puts · ' + puts});
    }
    return out;
}

function closeSideText(boxes: MavenCloseBoxSummary[], side: 'calls' | 'puts'): string | null {
    const parts: string[] = [];
    for (let i = 0; i < boxes.length; i++) {
        const b = boxes[i];
        const names = side === 'calls' ? b.callNames : b.putNames;
        const total = side === 'calls' ? b.callTotal : b.putTotal;
        if (!names || names.length === 0) {
            continue;
        }
        const inner = names.length === total ? 'ALL' : names.join(' · ');
        parts.push(b.label + ' (' + inner + ')');
    }
    return parts.length > 0 ? parts.join(' · ') : null;
}

// ---- Hedge Parameters (the section) ------------------------------------------------------------

// The section's cradle carries BOTH sides' parameters (owner, 2026-09-15, third pass): ONE BIG
// CHIP PER SIDE, each in its own shade — the side's whole parameter set in one wrapped chip,
// "OFF" for a side left unchecked so the reader can tell an unused side from a forgotten one.
// Both sides unchecked = nothing chosen, and that is the plain "Not Set" chip with no side.
// The key is the side's whole subtree ('calls.' / 'puts.'): the big chip is yellow when anything
// on that side changed. The "Not Set" chip answers to the two checkboxes.
export interface MavenSideCrumb extends MavenChip {
    side: 'calls' | 'puts' | null;   // null = the "Not Set" chip, no side shade
    keys: string[];                  // the prefixes this chip answers to (a chip may watch both sides)
}

export function hedgeParametersChips(calls: MavenSideInputs | null, puts: MavenSideInputs | null): MavenSideCrumb[] {
    const callsOn = !!calls && calls.enabled;
    const putsOn = !!puts && puts.enabled;
    if (!callsOn && !putsOn) {
        return [{side: null, key: null, keys: ['calls.enabled', 'puts.enabled'], text: MAVEN_NOT_SET_CHIP}];
    }
    return [
        {side: 'calls', key: 'calls.', keys: ['calls.'],
            text: 'Calls: ' + (callsOn ? chipTexts(sideChips(calls)).join(', ') : 'OFF')},
        {side: 'puts', key: 'puts.', keys: ['puts.'],
            text: 'Puts: ' + (putsOn ? chipTexts(sideChips(puts)).join(', ') : 'OFF')}
    ];
}

// ---- One side (Calls / Puts) -------------------------------------------------------------------

// Keys are RELATIVE to the side ('coverage.', 'offset', …); the side component prefixes its own
// 'calls.' / 'puts.' — it is the one that knows which side it is.
export function sideChips(side: MavenSideInputs | null | undefined): MavenChip[] {
    if (side && side.enabled && side.mode === 'preset') {
        return [chip('mode', 'Preset'), chip('presetId', side.presetName || (side.presetId ? 'Unavailable Preset' : 'Select Preset'))];
    }
    if (!side || !side.enabled) {
        // An unchecked side's one chip answers to the checkbox: a side switched off after a run
        // is a change, and this is the only chip there is to say so.
        return [chip('enabled', MAVEN_NOT_SET_CHIP)];
    }
    const chips: MavenChip[] = [];

    const cov = side.coverage;
    if (cov) {
        if (cov.kind === 'range') {
            // END strike only — Maven never knows the start before the run (S8), so there is no
            // "start–end" pair to print as the ORION does.
            chips.push(chip('coverage.', cov.rangeMode === 'distance'
                ? 'Range · Distance' + (isFiniteNumber(cov.rangeSize) ? ' · ' + cov.rangeSize + ' points' : '')
                : isFiniteNumber(cov.endStrike) ? 'Range · ' + cov.endStrike : 'Range'));
        } else if (cov.kind === 'followThrough') {
            chips.push(chip('coverage.', 'Follow-Through'));
        } else if (cov.kind === 'expectedRange') {
            chips.push(chip('coverage.', 'Expected Range'));
        }
    }

    if (isFiniteNumber(side.offsetFromShorts)) {
        chips.push(chip('offset', 'Offset · ' + side.offsetFromShorts));
    }

    const mp = side.minProfit;
    if (mp && isFiniteNumber(mp.amount)) {
        chips.push(chip('minProfit.',
            'Min Profit · ' + fmtMoney(mp.amount as number) + (mp.normalized ? ' · normalized' : '')));
    }

    const mr = side.maxRisk;
    if (mr) {
        if (mr.kind === 'dollar') {
            if (isFiniteNumber(mr.amount)) {
                chips.push(chip('maxRisk.',
                    'Max Risk · ' + fmtMoney(mr.amount as number) + (mr.normalized ? ' · normalized' : '')));
            }
        } else if (mr.level) {
            chips.push(chip('maxRisk.', 'Max Risk · ' + mr.level));
        }
    }

    return orNotSet(chips, 'enabled');
}

// ---- Directional Bias --------------------------------------------------------------------------

export function biasChips(inputs: RollPositionsInputs | null | undefined): MavenChip[] {
    const bias = inputs ? inputs.bias : null;
    if (!bias || !bias.enabled) {
        return [chip('bias.', 'Bias · No')];
    }
    if (!bias.direction) {
        return [chip('bias.', 'Bias · Yes')];
    }
    const dir = bias.direction;
    return [chip('bias.', 'Bias · ' + dir.charAt(0).toUpperCase() + dir.slice(1))];
}

// ---- Override Market Indicators (CR-20260915-001) ---------------------------------------------
//
// Owner ruling 2026-09-15 (#2): one chip per date the run targets — `date · IV x · HS y · VS z`
// for an overridden date, `date · market` for an untouched one; when NO date is overridden, one
// chip "All market". No dates yet = the section has nothing to say = "Not Set".
//
// VS shows the ENABLED side(s): one number for one side, `calls / puts` for both — the block
// itself renders one VS box per fieldset and the cradle folds them into one chip. IV is a FRACTION
// in the record (the OPG box stores 22.1% as 0.221) and is printed as the box prints it.
//
// Keys: a date's chip answers to that date's override record ('ovr.<date>.'); "All market" and
// "Not Set" answer to the whole subtree ('ovr.') — an override that was sent and has since been
// cleared is a change, and with no dated chip left to carry it, the one chip there is must.

export const MAVEN_ALL_MARKET_CHIP = 'All market';

export function overridesChips(
    dateKeys: string[],
    overrides: MarketConditionOverrides | null | undefined,
    callsOn: boolean,
    putsOn: boolean
): MavenChip[] {
    const keys = dateKeys || [];
    if (keys.length === 0) {
        return [chip('ovr.', MAVEN_NOT_SET_CHIP)];
    }
    const map = overrides || {};
    let anyOverridden = false;
    for (let i = 0; i < keys.length; i++) {
        const r = map[keys[i]];
        if (r && r.edited) {
            anyOverridden = true;
            break;
        }
    }
    if (!anyOverridden) {
        return [chip('ovr.', MAVEN_ALL_MARKET_CHIP)];
    }
    // An All-dates stamp puts the SAME record on every date, which would otherwise print the same
    // chip N times and bury the one fact worth reading. Collapse it to a single chip — but only on
    // PROOF that the range really is uniform (owner ruling 2026-09-17): a mixed range keeps its
    // per-date chips, because there the dates genuinely differ and one chip would hide that.
    //
    // 'ovr.' is the common prefix of every override diff key, so the collapsed chip's
    // changed-since-run tint is the union across the range — matching what the fieldset's own
    // boxes show on the same position.
    if (keys.length > 1 && isUniformAcrossDates(map, keys)) {
        const r = map[keys[0]];
        return [chip('ovr.', 'All dates · ' + fmtRecord(r, callsOn, putsOn))];
    }
    const chips: MavenChip[] = [];
    for (let i = 0; i < keys.length; i++) {
        const key = keys[i];
        const d = parseDayKey(key);
        const label = d ? fmtMavenDate(d) : key;
        const r = map[key];
        if (r && r.edited) {
            chips.push(chip('ovr.' + key + '.', label + ' · ' + fmtRecord(r, callsOn, putsOn)));
        } else {
            chips.push(chip('ovr.' + key + '.', label + ' · market'));
        }
    }
    return chips;
}

const MISSING_VALUE = '—';

// CR-20260921-002: every value is per side, so each prints "calls / puts" when both sides are
// on — the shape VS already had — and the one enabled side's value otherwise.
function fmtRecord(r: MarketConditionOverride, callsOn: boolean, putsOn: boolean): string {
    return 'IV ' + perSide(fmtIv(r.ivCall), fmtIv(r.ivPut), callsOn, putsOn)
        + ' · HS ' + perSide(fmtHs(r.hsCall), fmtHs(r.hsPut), callsOn, putsOn)
        + ' · VS ' + perSide(fmtVs(r.vsCall), fmtVs(r.vsPut), callsOn, putsOn);
}

function perSide(c: string, p: string, callsOn: boolean, putsOn: boolean): string {
    if (callsOn && putsOn) {
        return c + ' / ' + p;
    }
    return putsOn ? p : c;
}

function fmtIv(iv: number | null): string {
    return isFiniteNumber(iv) ? ((iv as number) * 100).toFixed(1) + '%' : MISSING_VALUE;
}

function fmtHs(hs: number | null): string {
    return isFiniteNumber(hs) ? (hs as number).toFixed(2) : MISSING_VALUE;
}

function fmtVs(vs: number | null): string {
    return isFiniteNumber(vs) ? '' + Math.round(vs as number) : MISSING_VALUE;
}
